/**
 * A requirement's ASSESSMENT CRITERIA (0099) — the versioned set of competencies its interviews are
 * evaluated against. Competencies can be added (from the vertical's library, or written by the team),
 * removed, re-weighted and re-worded; every save is a new version with a reason, and nothing already
 * recorded changes. Candidates evaluated on an older version are listed as out of date and can be
 * re-evaluated from the answers they already gave — nobody is re-interviewed.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customCompetencyAnchors, customCompetencyId, weightPercents } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";

export interface AssessmentCompetency {
  id: string;
  label: string;
  anchors: string;
  weight: number;
  source: "library" | "custom";
  kind?: string;
}
interface AssessmentSetJson {
  id: string;
  version: number;
  competencies: AssessmentCompetency[];
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}
export interface AssessmentResponse {
  current: AssessmentSetJson | null;
  history: AssessmentSetJson[];
  library: { id: string; label: string; anchors: string; weight: number; kind?: string }[];
  verticalName: string;
  outdatedCandidates: number;
  canEdit: boolean;
}

export const assessmentKey = (requisitionId: string) => ["assessment", requisitionId] as const;

export function useAssessment(requisitionId: string) {
  return useQuery({
    queryKey: assessmentKey(requisitionId),
    queryFn: () => api.get<AssessmentResponse>(`/requisitions/${requisitionId}/assessment`),
    enabled: requisitionId.length > 0,
  });
}

/** Compact read-only summary: each competency's share of the criteria, custom ones marked. */
export function AssessmentBars({ set }: { set: AssessmentSetJson }): ReactNode {
  const ids = set.competencies.map((c) => c.id);
  const pct = weightPercents(ids, new Map(set.competencies.map((c) => [c.id, c.weight])));
  const max = Math.max(1, ...pct);
  const rows = set.competencies
    .map((c, i) => ({ ...c, pct: pct[i] ?? 0 }))
    .sort((a, b) => b.pct - a.pct);
  return (
    <ul className="t1-bars">
      {rows.map((w) => (
        <li key={w.id}>
          <div className="bl">
            <span title={w.label}>
              {w.label}
              {w.source === "custom" && <span className="pill t" style={{ marginLeft: 6 }}>custom</span>}
            </span>
            <b className="tnum">{w.pct}%</b>
          </div>
          <div className="bt" aria-hidden="true">
            <i style={{ width: `${(w.pct / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function AssessmentEditor({ requisitionId }: { requisitionId: string }): ReactNode {
  const qc = useQueryClient();
  const q = useAssessment(requisitionId);
  const [draft, setDraft] = useState<AssessmentCompetency[] | null>(null);
  const [reason, setReason] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [msg, setMsg] = useState<{ tone: "good" | "crit" | "info"; text: string } | null>(null);
  useEffect(() => {
    if (q.data?.current && draft === null) setDraft(q.data.current.competencies);
  }, [q.data, draft]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: assessmentKey(requisitionId) });
    void qc.invalidateQueries({ queryKey: ["funnel", requisitionId] });
    void qc.invalidateQueries({ queryKey: ["report"] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.put<{ changed: boolean; current: AssessmentSetJson; outdatedCandidates: number }>(
        `/requisitions/${requisitionId}/assessment`,
        { competencies: draft, reason },
      ),
    onSuccess: (res) => {
      setReason("");
      setDraft(res.current.competencies);
      setMsg(
        res.changed
          ? {
              tone: "good",
              text: `Saved as criteria v${res.current.version}.${res.outdatedCandidates > 0 ? ` ${res.outdatedCandidates} interviewed candidate${res.outdatedCandidates === 1 ? " is" : "s are"} now on older criteria — re-evaluate below to compare them on v${res.current.version}.` : ""}`,
            }
          : { tone: "info", text: "No change." },
      );
      invalidate();
    },
    onError: (e: unknown) => setMsg({ tone: "crit", text: e instanceof ApiError ? e.message : "Couldn't save the criteria." }),
  });
  const reEvaluate = useMutation({
    mutationFn: (scope: "outdated" | "all") =>
      api.post<{ queued: number; eligible: number; assessmentSetVersion: number }>(
        `/requisitions/${requisitionId}/evaluations/re-evaluate`,
        { reason: reason.trim().length >= 4 ? reason : `Bring evaluations up to criteria v${q.data?.current?.version ?? "?"}`, scope },
      ),
    onSuccess: (res) => {
      setMsg({
        tone: "good",
        text: `${res.queued} re-evaluation${res.queued === 1 ? "" : "s"} queued against v${res.assessmentSetVersion} — from the answers already given; no candidate is contacted.`,
      });
      invalidate();
    },
    onError: (e: unknown) => setMsg({ tone: "crit", text: e instanceof ApiError ? e.message : "Couldn't queue re-evaluation." }),
  });

  const current = q.data?.current ?? null;
  const dirty = useMemo(
    () => !!draft && !!current && JSON.stringify(draft) !== JSON.stringify(current.competencies),
    [draft, current],
  );
  if (q.isError) return <p className="mut sm">Couldn't load the assessment criteria.</p>;
  if (q.isPending || !q.data) return <p className="mut sm">Loading criteria…</p>;
  if (!current || !draft) return <p className="mut sm">This requirement has no criteria yet — pick competencies first.</p>;

  const canEdit = q.data.canEdit;
  const inSet = new Set(draft.map((c) => c.id));
  const addable = q.data.library.filter((l) => !inSet.has(l.id));
  const pct = weightPercents(
    draft.map((c) => c.id),
    new Map(draft.map((c) => [c.id, c.weight])),
  );
  const update = (id: string, patch: Partial<AssessmentCompetency>): void => {
    setDraft(draft.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    setMsg(null);
  };
  const newId = customCompetencyId(newLabel);
  const newIdTaken = !!newId && (inSet.has(newId) || q.data.library.some((l) => l.id === newId));

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="mut sm">
        Criteria <b>v{current.version}</b>
        {current.reason ? ` — “${current.reason}”` : ""} · {new Date(current.createdAt).toLocaleDateString()}. Questions
        collect evidence; after each interview the evaluator decides which answers evidence which criteria, so a
        competency added later is evaluated against answers already given.
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {draft.map((c, i) => (
          <div key={c.id} className="card pad" style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {c.source === "custom" ? (
                <input
                  className="rq-in"
                  style={{ flex: 1, minWidth: 160 }}
                  value={c.label}
                  disabled={!canEdit}
                  onChange={(e) => update(c.id, { label: e.target.value })}
                  aria-label="Competency name"
                />
              ) : (
                <b style={{ flex: 1 }}>{c.label}</b>
              )}
              <span className={c.source === "custom" ? "pill t" : "pill n"}>{c.source === "custom" ? "custom" : "library"}</span>
              <label className="mut sm" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                weight
                <input
                  className="rq-in tnum"
                  type="number"
                  min={1}
                  max={100}
                  style={{ width: 64 }}
                  value={c.weight}
                  disabled={!canEdit}
                  onChange={(e) => update(c.id, { weight: Math.max(1, Math.min(100, Math.round(Number(e.target.value) || 1))) })}
                />
                <span className="tnum">{pct[i] ?? 0}%</span>
              </label>
              {canEdit && draft.length > 1 && (
                <button type="button" className="btn sm ghost" onClick={() => setDraft(draft.filter((x) => x.id !== c.id))}>
                  Remove
                </button>
              )}
            </div>
            {c.source === "custom" ? (
              <textarea
                className="rq-in"
                style={{ minHeight: 70, fontFamily: "inherit", fontSize: 12.5 }}
                value={c.anchors}
                disabled={!canEdit}
                onChange={(e) => update(c.id, { anchors: e.target.value })}
                aria-label="Score bands"
              />
            ) : (
              <details>
                <summary className="mut sm">Score bands</summary>
                <p className="sm" style={{ whiteSpace: "pre-wrap" }}>{c.anchors}</p>
              </details>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <div style={{ display: "grid", gap: 8 }}>
          {addable.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span className="mut sm">Add from {q.data.verticalName}:</span>
              {addable.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="btn sm ghost"
                  onClick={() => setDraft([...draft, { id: l.id, label: l.label, anchors: l.anchors, weight: l.weight, source: "library", ...(l.kind ? { kind: l.kind } : {}) }])}
                >
                  + {l.label}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <input
              className="rq-in"
              style={{ flex: 1, minWidth: 200 }}
              placeholder="Add your own competency — e.g. Business Central finance configuration"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <button
              type="button"
              className="btn sm"
              disabled={newLabel.trim().length < 2 || !newId || newIdTaken || draft.length >= 20}
              title={newIdTaken ? "A competency with this name is already in the criteria or the library" : undefined}
              onClick={() => {
                setDraft([...draft, { id: newId, label: newLabel.trim(), anchors: customCompetencyAnchors(newLabel), weight: 20, source: "custom" }]);
                setNewLabel("");
              }}
            >
              Add
            </button>
          </div>
          <span className="mut sm">
            A custom competency gets default bands that separate direct experience from transferable, comparable work —
            edit them to say what each band means for this role.
          </span>
          <textarea
            className="rq-in"
            style={{ minHeight: 44, fontFamily: "inherit", fontSize: 12.5 }}
            placeholder="Why are the criteria changing? (required — recorded with the new version)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              className="btn sm primary"
              disabled={!dirty || reason.trim().length < 4 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : `Save as v${current.version + 1}`}
            </button>
            <button type="button" className="btn sm ghost" disabled={!dirty} onClick={() => setDraft(current.competencies)}>
              Discard changes
            </button>
          </div>
        </div>
      )}

      <div className="card pad" style={{ display: "grid", gap: 8 }}>
        <b className="sm">Re-evaluate interviewed candidates</b>
        <span className="mut sm">
          {q.data.outdatedCandidates > 0
            ? `${q.data.outdatedCandidates} interviewed candidate${q.data.outdatedCandidates === 1 ? " is" : "s are"} not yet evaluated on v${current.version} — they are shown but not ranked against the rest.`
            : `Every interviewed, undecided candidate is evaluated on v${current.version}.`}{" "}
          Re-evaluation reads the answers already given; the candidate is never contacted, and a failed run never
          replaces an existing result.
        </span>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn sm primary"
              disabled={q.data.outdatedCandidates === 0 || reEvaluate.isPending || dirty}
              onClick={() => reEvaluate.mutate("outdated")}
            >
              Re-evaluate {q.data.outdatedCandidates} out of date
            </button>
            <button type="button" className="btn sm ghost" disabled={reEvaluate.isPending || dirty} onClick={() => reEvaluate.mutate("all")}>
              Re-evaluate everyone
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div className={`banner ${msg.tone === "crit" ? "crit" : msg.tone === "good" ? "info" : "info"}`} style={{ margin: 0 }} role="status">
          <span>{msg.text}</span>
        </div>
      )}

      {q.data.history.length > 1 && (
        <details>
          <summary className="mut sm">Version history ({q.data.history.length})</summary>
          <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
            {q.data.history.map((h) => (
              <div key={h.id} className="sm">
                <b>v{h.version}</b> · {new Date(h.createdAt).toLocaleString()} · {h.competencies.map((c) => c.label).join(", ")}
                {h.reason ? <span className="mut"> — “{h.reason}”</span> : null}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
